import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  Label,
} from 'recharts';
import { COLOR_GREY_200, COLOR_GREY_600 } from 'src/utils/colors';
import { DynamicBarChartPropTypes } from 'src/interfaces/iproptypes';
import { DataChartTooltip } from 'src/App/Dashboards/Market/Components/Tooltips';
import { abbreviateNumber, convertCurrency, formatCurrency } from 'src/utils/functions';
import { logAmplitudeEvent } from 'src/utils/analytics';

const DynamicBarChart = ({
  layout,
  data,
  unit,
  label,
  height,
  barColor,
  barAccentColor,
  useShortHand,
  isCurrency,
  isPropertySizeGraph,
  negitiveBarColor,
  negativeBarAccentColor,
  getTooltipText,
  tooltipTextSuffix,
  tickFormattorXAxis,
  parent,
  xAxisLabelOrientation,
  addSignPrefix = false,
  ref,
}: DynamicBarChartPropTypes) => {
  const [focusBar, setFocusBar] = useState(null);

  const CustomTooltip = ({ active, payload, label, classes }) => {
    useEffect(() => {
      if (active && parent) {
        logAmplitudeEvent('tooltip_interacted', {
          tooltip_type: 'graph',
          parent: parent,
        });
      }
    }, [active]);

    if (active && payload && payload.length > 0) {
      const tooltipData = [
        {
          dataKey: payload[0].payload.keyLong,
          val:
            (tooltipTextSuffix ? ' ' + `${tooltipTextSuffix}: ` : ' ') +
            (getTooltipText
              ? getTooltipText(payload[0].value, payload[0].payload.keyLong)
              : isCurrency
              ? `${unit}${addSignPrefix ? (payload[0].value >= 0 ? '+' : '-') : ''}${formatCurrency(
                  payload[0].value.toString()
                )}`
              : `${addSignPrefix ? (payload[0].value >= 0 ? '+' : '-') : ''}${formatCurrency(
                  payload[0].value.toString()
                )}${unit}`),
          color: barAccentColor,
        },
      ];
      return <DataChartTooltip data={tooltipData} />;
    }
    return null;
  };

  const getBarColor = (index: number, value: number) => {
    const isValueNegative = value && value < 0;

    if (focusBar === index) {
      return isValueNegative && negativeBarAccentColor ? negativeBarAccentColor : barAccentColor;
    }

    return isValueNegative && negitiveBarColor ? negitiveBarColor : barColor;
  };

  return (
    <ResponsiveContainer height={height} width="100%">
      <BarChart
        layout={layout}
        data={data}
        margin={
          layout === 'horizontal'
            ? { top: 5, right: 0, bottom: 5, left: -30 }
            : { top: 5, right: 0, bottom: 5, left: 15 }
        }
        onMouseMove={state => {
          if (state.isTooltipActive) {
            setFocusBar(state.activeTooltipIndex);
          } else {
            setFocusBar(null);
          }
        }}
      >
        <XAxis
          dataKey={layout === 'horizontal' ? (useShortHand ? 'keyShort' : 'keyLong') : ''}
          style={{
            fontSize: '0.75rem',
            fontFamily: 'Roboto',
          }}
          axisLine={false}
          tickLine={false}
          type={layout === 'vertical' ? 'number' : 'category'}
          tickFormatter={value => (tickFormattorXAxis ? tickFormattorXAxis(value) : value)}
          angle={xAxisLabelOrientation === 'vertical' ? 315 : 0}
          interval={0}
        >
          {label && (
            <Label
              value={label}
              offset={0}
              position="insideBottom"
              style={{
                fontSize: '0.75rem',
                fontFamily: 'Roboto',
              }}
              fill={COLOR_GREY_600}
            />
          )}
        </XAxis>
        <YAxis
          dataKey={layout === 'vertical' ? (useShortHand ? 'keyShort' : 'keyLong') : ''}
          style={{
            fontSize: '0.75rem',
            fontFamily: 'Roboto',
          }}
          axisLine={false}
          tickLine={false}
          type={layout === 'horizontal' ? 'number' : 'category'}
          tickSize={isCurrency ? 2 : 4}
          tickFormatter={value => {
            return isCurrency && !isNaN(value)
              ? abbreviateNumber(value, false)
              : isPropertySizeGraph && (Number(value) > 999 || Number(value) < -999)
              ? abbreviateNumber(value, true)
              : value;
          }}
        />
        <CartesianGrid
          vertical={layout === 'horizontal' ? false : true}
          horizontal={layout === 'vertical' ? false : true}
          stroke={COLOR_GREY_200}
        />
        <Tooltip content={<CustomTooltip classes active payload label />} position={{ y: -50 }} />
        <Bar
          dataKey="value"
          isAnimationActive={true}
          animationDuration={150}
          radius={[13, 13, 13, 13]}
          barSize={13}
          style={{
            cursor: 'pointer',
          }}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getBarColor(index, entry.value)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default DynamicBarChart;
